import styled from "@emotion/styled";
import { Box, CardActionArea, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toCurrency, toPercent } from "@helpers";
import { GainLossTypography, LinearProgressBar } from "@rentablo/components";
import { AssetClass } from "@api/v1/hooks/performance/assetClasses";
import { SmallCard } from "../SmallCard";

// Pixel strings on purpose: in sx a plain 1 means 100%, and a pill-sized
// box would make the strip scrollable.
const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

// Active looks as on the desktop cards.
const Pill = styled(SmallCard, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ theme, active }) => ({
  flex: "none",
  minWidth: theme.spacing(38),
  scrollSnapAlign: "start",
  ...(active && {
    backgroundColor: theme.palette.background.selected,
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  }),
}));

const PillAction = styled(CardActionArea)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2.5, 3, 3),
}));

const Marker = styled.span<{ color: string }>(({ color }) => ({
  flex: "none",
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: color,
}));

export interface AssetClassPillItem {
  key: AssetClass;
  title: string;
  color: string;
  value?: number;
  percent?: number;
  increase?: number;
}

interface Props {
  item: AssetClassPillItem;
  active: boolean;
  onToggle: (assetClass: AssetClass) => void;
  currency?: string;
}

export const AssetClassPill = ({ item, active, onToggle, currency }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const share = (item.percent ?? 0) < 1 ? "< 1" : String(item.percent);

  return (
    <Pill active={active}>
      <PillAction
        aria-pressed={active}
        onClick={() => onToggle(item.key)}
        data-testid="asset-class-pill"
      >
        <Box component="span" display="flex" alignItems="center" gap={1.5}>
          <Marker aria-hidden color={item.color} />
          <Typography
            component="span"
            variant="caption"
            color="textSecondary"
            noWrap
          >
            {item.title}
          </Typography>
        </Box>
        <Box
          component="span"
          display="flex"
          alignItems="baseline"
          justifyContent="space-between"
          gap={3}
          whiteSpace="nowrap"
        >
          <Typography component="span" variant="body4">
            {toCurrency(item.value, currency, false, {
              maximumFractionDigits: 0,
            })}
          </Typography>
          {item.increase != null && (
            <Box component="span" display="flex" alignItems="baseline" gap={1}>
              <Typography
                component="span"
                variant="overline2"
                color="textSecondary"
              >
                {t("components.AssetTypeCard.ytd")}
              </Typography>
              <GainLossTypography
                component="span"
                variant="caption2"
                value={item.increase}
              >
                {toPercent(item.increase, "exceptZero")}
              </GainLossTypography>
            </Box>
          )}
        </Box>
        {/* The class's share of the portfolio; a sliver stays visible for
            the smallest ones. */}
        <Box component="span" display="block" aria-hidden>
          <LinearProgressBar
            progress={Math.max(item.percent ?? 0, 1)}
            color={item.color}
            bgColor={theme.palette.background.neutralDark}
            thickness={3}
            borderRadius="2px"
          />
        </Box>
        <Box component="span" sx={visuallyHidden}>
          {t("assetTypeBreakdown.filter.share", { percent: share })}
        </Box>
      </PillAction>
    </Pill>
  );
};
